import type { ToastProps } from '@nuxt/ui';
import type { Toast } from '@nuxt/ui/composables';
import type { MaybeRefOrGetter } from 'vue';
import { useOverlay, useToast } from '@nuxt/ui/composables';
import { defineAsyncComponent, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { useErrorFormatter } from '~/composables/use-error-formatter';

const LazyErrorDetailDialog = defineAsyncComponent(() => import('~/components/Error/ErrorDetailDialog.vue'));

export interface UseToastResultHandlingOptions {
	success?: MaybeRefOrGetter<Partial<ToastProps>>;
	error?: MaybeRefOrGetter<Partial<ToastProps>>;
}

type ToastWithType = Partial<Toast> & { 'data-type': string };

export function useToastResultHandling(options?: UseToastResultHandlingOptions) {
	const toaster = useToast();
	const { formatError } = useErrorFormatter();
	const { t } = useI18n();

	return {
		onSuccess: (overrides?: UseToastResultHandlingOptions['success']) => toaster.add({
			'color': 'success',
			'icon': 'lucide:circle-check',
			'data-type': 'success',
			...toValue(options?.success),
			...overrides,
		} as ToastWithType),
		onError: (error: any, overrides?: UseToastResultHandlingOptions['error']) => {
			const toastOptions = {
				...toValue(options?.error),
				...overrides,
			};
			return toaster.add({
				'color': 'error',
				'description': formatError(error),
				'icon': 'lucide:circle-x',
				'data-type': 'error',
				'actions': error
					? [{
							label: t('common.view_details'),
							variant: 'ghost',
							color: 'neutral',
							class: '-ms-2',
							trailingIcon: 'lucide:chevron-right',
							onClick: () => {
								useOverlay().create(LazyErrorDetailDialog, {
									defaultOpen: true,
									props: {
										title: typeof toastOptions.title === 'string' ? toastOptions.title : undefined,
										error,
									},
								});
							},
						}]
					: undefined,
				...toastOptions,
			} as ToastWithType);
		},
	};
}
